// Tiny fetch wrapper for the dial-api worker. Reads VITE_DIAL_API_URL,
// falling back to VITE_API_BASE_URL (older / generic name) so existing
// Cloudflare Pages env configs continue to work.
//
// In production the auth bridge in apps/dial/src/auth registers a Bearer
// provider via setAccessTokenProvider; in dev the X-Dev-User-Id fallback
// path keeps `wrangler dev` workflows working without a real magic link.

const apiBaseUrl =
  import.meta.env.VITE_DIAL_API_URL?.trim() || import.meta.env.VITE_API_BASE_URL?.trim() || '';
const devUserId = import.meta.env.VITE_DEV_USER_ID?.trim() ?? '';
const supabaseConfigured = (import.meta.env.VITE_SUPABASE_URL?.trim() ?? '').length > 0;

export const isDialApiConfigured =
  apiBaseUrl.length > 0 && (supabaseConfigured || devUserId.length > 0);

export interface RequestInitJson {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public payload: unknown,
  ) {
    super(`dial-api error (${status})`);
  }
}

type AccessTokenProvider = () => Promise<string | null>;
let accessTokenProvider: AccessTokenProvider | null = null;

/** Auth providers (see apps/dial/src/auth/) call this once after mount so
 * the fetch wrapper can read the current bearer on every call. */
export function setAccessTokenProvider(fn: AccessTokenProvider | null): void {
  accessTokenProvider = fn;
}

export async function dialApiFetch<T>(path: string, init: RequestInitJson = {}): Promise<T> {
  if (!isDialApiConfigured) {
    throw new ApiError(0, { error: 'dial_api_not_configured' });
  }
  const headers: HeadersInit = {};
  if (supabaseConfigured) {
    const token = accessTokenProvider ? await accessTokenProvider() : null;
    if (!token) {
      // Caller is signed out. Surface as 401 so UI can redirect to login.
      throw new ApiError(401, { error: 'not_authenticated' });
    }
    headers['authorization'] = `Bearer ${token}`;
  } else {
    headers['x-dev-user-id'] = devUserId;
  }
  let body: BodyInit | undefined;
  if (init.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(init.body);
  }
  const res = await fetch(new URL(path, apiBaseUrl), {
    method: init.method ?? 'GET',
    headers,
    body,
    signal: init.signal,
  });
  if (!res.ok) {
    const payload: unknown = await res.json().catch(() => ({}));
    throw new ApiError(res.status, payload);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
